import { SHARE_SEARCH_URL_PARAM } from '@aibees/elephant/context/search-params-url-sync';
import { useSearchTableScope } from '@aibees/elephant/context/search-table-scope';
import {
  useSearchParamsStore,
  type SearchTableScope,
} from '@aibees/elephant/store/search-params-store';
import {
  ElephantPresetType,
  type SearchBrowseParams,
} from '@aibees/elephant/types';
import { useCreatePresetMutationApi } from '@aibees/shared/api';
import { useCopyToClipboard, useNotify } from '@aibees/shared/hooks';
import { objSanitizeNilValues, stableHash } from '@aibees/shared/utils';
import { useCallback, useState } from 'react';

type ScopeParams = Partial<SearchBrowseParams>;

/** Compact, fixed-length digest of the canonical (key-sorted, value-inclusive)
 *  serialization of the params. Used as the preset name so identical filter sets
 *  map to the same shared preset (the create endpoint dedups by name). */
function paramsFingerprint(params: ScopeParams): string {
  const canonical = stableHash(params);
  let hash = 5381;
  for (let i = 0; i < canonical.length; i++) {
    hash = (hash * 33) ^ canonical.charCodeAt(i);
  }
  return `share_${(hash >>> 0).toString(36)}`;
}

function readScopeParams(scope: SearchTableScope): ScopeParams {
  const state = (
    useSearchParamsStore as unknown as {
      __store: {
        getState: () => {
          paramsByScope: Record<SearchTableScope, ScopeParams>;
        };
      };
    }
  ).__store.getState();
  return objSanitizeNilValues(state.paramsByScope[scope] ?? {}) as ScopeParams;
}

function buildShareUrl(presetId: string): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}?${SHARE_SEARCH_URL_PARAM}=${presetId}`;
}

/**
 * Creates (or reuses) a `SHARE_SEARCH` preset holding the current scope's filter
 * params, then copies a `?search-id=<id>` link to the clipboard.
 *
 * Dedup is atomic on the backend: `returnDuplicatedValue: true` returns the
 * existing preset with the fingerprint name, or creates and returns a new one —
 * so identical filter sets always resolve to the same shared preset in a single
 * request, with no client-side collision handling.
 */
export function useShareSearchLink() {
  const scope = useSearchTableScope();
  const notify = useNotify();
  const { copy } = useCopyToClipboard();
  const createShare = useCreatePresetMutationApi<ScopeParams>(
    false,
    true,
    true,
    true
  );
  const [isSharing, setIsSharing] = useState(false);

  const share = useCallback(async () => {
    if (isSharing) return;
    if (scope !== 'company' && scope !== 'people' && scope !== 'employees') {
      return;
    }

    setIsSharing(true);
    try {
      const params = readScopeParams(scope);
      const created = await createShare.mutateAsync({
        name: paramsFingerprint(params),
        type: ElephantPresetType.SHARE_SEARCH,
        value: params,
      });

      if (!created?.id) {
        notify.error('Could not create the share link. Please try again.');
        return;
      }

      const ok = await copy(buildShareUrl(created.id));
      if (ok) {
        notify.success('Successfully Copied!');
      } else {
        notify.error('Could not copy the share link to your clipboard.');
      }
    } catch {
      notify.error('Could not create the share link. Please try again.');
    } finally {
      setIsSharing(false);
    }
  }, [isSharing, scope, createShare, notify, copy]);

  return { share, isSharing };
}
